import React from "react";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../../assets/layouts";
import {
  SearchDto,
} from "../../../../assets/utils/lib/searchDto";
import { CategoryService } from "./categoryService";
import { Constants } from "../../../../assets/utils/lib/constants";
import {
  defaultSearchQuery,
  GRID_ACTION_TYPES,
  SetState,
} from "../../../../assets/types";

import Control from "../../../../assets/vfx/controls/control";
import { ControlDataGridColumnProps } from "../../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import { createActionColumn, createLayoutOptions } from "../../../../assets/configuration/gridConfig";
import { CommunityCategoryDto } from "../../../../services/dto";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/store";
import { ClaimHelper } from "../../../../assets/utils/helpers";
import { useCommunity } from "../../../../context/communityContext";

interface CategoryPageProps {
  controlStyles?: IControlStyles;
  communityId?: string;
  hideTitle?: boolean;
  setCommunity: SetState<string | undefined>;
  onCustomEvent?: (actionType: string, dataId: string) => void;
}

const DATA_ID_FIELD = "id";
const COMMUNITY_ID_FIELD = "communityId";

const getCategorySearchQuery = (communityId?: string): SearchDto => {
  if (!communityId || communityId === Constants.emptyGuid) {
    return defaultSearchQuery;
  }

  return {
    ...defaultSearchQuery,
    filters: [
      ...defaultSearchQuery.filters.filter((filter) => filter.field !== COMMUNITY_ID_FIELD),
      {
        field: COMMUNITY_ID_FIELD,
        value: communityId,
      },
    ],
  };
};

const createGridColumns = (): ControlDataGridColumnProps[] => [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "name",
    title: "Name",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "18%",
  },
  {
    data: "description",
    title: "Description",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "34%",
  },
  {
    data: "activationDate",
    title: "Activation Date",
    visible: true,
    columnType: "date",
    alignment: "left",
    width: "24%",
  },
];

 
export const CategoryPage = (props: CategoryPageProps) => {
  const styles = props.controlStyles ?? useControlStyles();
  const { isSingleCommunityUser: isSingleCommunityUserFromStore } = useSelector((state: RootState) => state.layout);
  const { communityId: contextCommunityId } = useCommunity();
  const claimCommunityId = ClaimHelper.getCommunityId() || undefined;
  const resolvedCommunityId = props.communityId ?? claimCommunityId ?? contextCommunityId ?? undefined;
  const [searchQuery, setSearchQuery] = React.useState<SearchDto>(
    getCategorySearchQuery(
      isSingleCommunityUserFromStore || resolvedCommunityId
        ? resolvedCommunityId
        : undefined
    )
  );

  React.useEffect(() => {
    setSearchQuery((query) => ({
      ...query,
      filters: [
        ...query.filters.filter((filter) => filter.field !== COMMUNITY_ID_FIELD),
        ...(resolvedCommunityId
          ? [{
            field: COMMUNITY_ID_FIELD,
            value: resolvedCommunityId,
          }]
          : []),
      ],
    }));
  }, [resolvedCommunityId]);
  const categoryGridQuery = CategoryService.gridQuery(searchQuery);
  const isGridLoading = categoryGridQuery.isLoading || categoryGridQuery.isFetching;

  const categorys: CommunityCategoryDto[] =
    categoryGridQuery.data?.result ?? [];

  const hasData: boolean = categorys.length > 0;
  const gridColumns: ControlDataGridColumnProps[] = createGridColumns();
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
    GRID_ACTION_TYPES.DELETE,
  ]);
  const layoutOptions = createLayoutOptions(hasData);

  const handleNew = (): void =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
      <PageContent hideHeader>
        <Control.DataGrid
          dataId={DATA_ID_FIELD}
          cols={gridColumns}
          data={categorys}
          actionCol={actionColumn}
          layoutOptions={layoutOptions}
          onCustomEvent={props.onCustomEvent}
          totalRecords={
            categoryGridQuery.data?.totalSize ?? categorys?.length ?? 0
          }
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) => {
            setSearchQuery((query) => ({
              ...query,
              ...patch,
              sorts: patch.sortBy ? [patch.sortBy] : query.sorts,
            }));
          }}
          uiOptions={{
            title: props.hideTitle ? undefined : "Categories",
            primaryAction: {
              text: "Add Category",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}
        />
    </PageContent>
  );
};
